import { AfterViewInit, ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { ImportExportService } from '@app/common/services/import-export-service';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { CEMImportTypes } from '@app/shared/models/cem-import-types';
import { ImportExportMetadata } from '@app/shared/models/import-export-metadata';
import { ButtonDirective, SplitButtonComponent } from '@progress/kendo-angular-buttons';
import { DatePickerComponent } from '@progress/kendo-angular-dateinputs';
import { DialogActionsComponent, DialogComponent, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { TabStripComponent, TabStripModule } from '@progress/kendo-angular-layout';
import { saveAs } from '@progress/kendo-file-saver';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ForecastScenarioDetailComponent } from '../forecast-scenario-detail/forecast-scenario-detail.component';
import { ForecastScenariosComponent } from '../forecast-scenarios/forecast-scenarios.component';
import { ForecastBrokerService } from '../services/forecast-broker.service';
import { ForecastService } from '../services/forecast.service';
import { UpliftsComponent } from '../uplifts/uplifts.component';

@Component({
  selector: 'app-forecast-base',
  templateUrl: './forecast-base.component.html',
  styleUrls: ['./forecast-base.component.scss'],
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    DatePickerComponent,
    DialogActionsComponent,
    DialogComponent,
    ForecastScenarioDetailComponent,
    ForecastScenariosComponent,
    FormsModule,
    SplitButtonComponent,
    TabStripModule,
    UpliftsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    DialogService,
  ],
})
export class ForecastBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  private zone = inject(NgZone);
  private broker = inject(ForecastBrokerService);
  private dialogService = inject(DialogService);
  private service = inject(ForecastService);
  private importExportService = inject(ImportExportService);
  private store = inject(AppStorage);

  public environment = inject(PlexEnvironment);
  public icons = kendoIcons;

  public readonly tabstrip = viewChild<TabStripComponent>('forecastTabStrip');

  public startDate: Date = moment().toDate();
  public endDate: Date = moment().add(3, 'years').toDate();

  effectiveDate: Date = moment().add(this.environment.capitalForecastDefaultEffectiveDateOffset, 'days').toDate();
  userComment: string = '';
  showUpdateCapitalForecastDialog: boolean = false;

  public selectedScenarioId?: number = null;
  public selectedParentBuildId: string = '';
  public selectedCapitalForecastLogId?: number = null;
  public selectedTab?: string = null;
  public tabItems = new Array<string>();
  public tabMeta = new Map<string, { parentBuildId: string; scenarioId: number; capitalForecastLogId: number; }>();
  public selectedTabIndex: number = 0;
  public loading: boolean = false;
  public isShowingDownloadDialog: boolean = false;
  public detailTypeDescription: string = '';
  public selectedImportType: CEMImportTypes;
  public CEMImportTypes = CEMImportTypes;
  public isReadOnly: boolean = false;

  private selectSub: Subscription;
  private dblClickedSub: Subscription;

  exportButtonData: Array<any> = [
    {
      key: 'drilling',
      text: 'Drilling',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.DrillingForecastDetail) }
    },
    {
      key: 'completions',
      text: 'Completions',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.CompletionsForecastDetail); }
    },
    {
      key: 'wellconnect',
      text: 'Well Connect',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.WellConnectForecastDetail); }
    },
    {
      key: 'flowline',
      text: 'Flowline',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.FlowlineForecastDetail, true); }
    },
    {
      key: 'pipeline',
      text: 'Pipeline',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.PipelineForecastDetail, true); }
    },
    {
      key: 'tankBattery',
      text: 'Tank Battery',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.TankBatteryForecastDetail, true); }
    },
    {
      key: 'padConstruction',
      text: 'Pad Construction',
      disabled: false,
      click: () => { this.showDownloadDialog(CEMImportTypes.PadConstructionForecastDetail); }
    }
  ];

  ngOnInit() {
    this.selectSub = this.broker.scenarioSelectionChanged.subscribe(scenario => {
      if (scenario != null) {
        this.isReadOnly = scenario.isReadOnly;
        this.selectedScenarioId = scenario.scenarioId;
        this.selectedParentBuildId = scenario.parentBuildId;
        this.selectedCapitalForecastLogId = scenario.capitalForecastLogId;
        this.selectedTab = scenario.capitalForecastLogId == null ? scenario.scenarioId : `${scenario.scenarioId}-${scenario.capitalForecastLogId}`;
        this.tabMeta.set(this.selectedTab, { parentBuildId: scenario.parentBuildId, scenarioId: scenario.scenarioId, capitalForecastLogId: scenario.capitalForecastLogId });
      }
    });

    this.dblClickedSub = this.broker.cellDoubleClicked.subscribe(scenario => {
      if (scenario != null) {
        this.isReadOnly = scenario.isReadOnly;
        this.selectedScenarioId = scenario.scenarioId;
        this.selectedParentBuildId = scenario.parentBuildId;
        this.selectedCapitalForecastLogId = scenario.capitalForecastLogId;
        this.selectedTab = scenario.capitalForecastLogId == null ? scenario.scenarioId : `${scenario.scenarioId}-${scenario.capitalForecastLogId}`;
        if (this.selectedScenarioId != null) {
          this.tabMeta.set(this.selectedTab, { parentBuildId: scenario.parentBuildId, scenarioId: scenario.scenarioId, capitalForecastLogId: scenario.capitalForecastLogId });
          this.openScenario();
        }
      }
    });

    this.zone.onMicrotaskEmpty.subscribe({
      next: () => {
        this.zone.run(() => {
          if (this.desiredTabIndex > 0) {
            this.tabstrip().selectTab(this.desiredTabIndex);
            this.selectedTabIndex = this.desiredTabIndex;
            this.desiredTabIndex = -1;

          }
        });
      }
    });
  }

  ngAfterViewInit() {
    this.tabstrip().tabSelect.subscribe(tab => {
      this.selectedTabIndex = tab.index;
      if (this.selectedTabIndex >= 2) {
        let tabMetaValues = Array.from(this.tabMeta.values());
        this.selectedScenarioId = Number(tabMetaValues[this.selectedTabIndex - 2].scenarioId);
        this.selectedParentBuildId = tabMetaValues[this.selectedTabIndex - 2].parentBuildId;
        this.selectedCapitalForecastLogId = Number(tabMetaValues[this.selectedTabIndex - 2].capitalForecastLogId);
      }
    });

    this.store.knownEvents.assetTeamSelected.subscribe(selected => {
      if (!selected) return;
      this.closeAllTabs();
    });
  }

  ngOnDestroy(): void {
    if (this.selectSub) {
      this.selectSub.unsubscribe();
    }

    if (this.dblClickedSub) {
      this.dblClickedSub.unsubscribe();
    }
  }

  private desiredTabIndex = -1;

  public openScenario() {
    let existingItemIndex = this.tabItems.indexOf(this.selectedTab);
    if (existingItemIndex == -1) {
      this.tabItems.push(this.selectedTab);

      // this will select it on the next zone refresh
      this.desiredTabIndex = this.tabItems.length + 1;
    } else {
      this.desiredTabIndex = existingItemIndex + 2;
    }
    this.zone.onMicrotaskEmpty.emit(null);
  }

  public async updateCapital() {
    this.showUpdateCapitalForecastDialog = true;
  }

  public closeTab(index: number) {
    this.tabstrip().selectTab(0);
    this.selectedTabIndex = 0;
    this.tabItems.splice(index, 1);
  }

  public closeAllTabs() {
    this.tabstrip().selectTab(0);
    this.selectedTabIndex = 0;
    this.tabItems.splice(0, this.tabItems.length);
  }

  exportButtonLogic(): void {
    this.exportButtonData.forEach(x => {
      x.disabled = this.loading;
    });
  }

  public downloadDetailedForecastSheet() {
    this.isShowingDownloadDialog = false;
    this.loading = true;

    var metadata = new Array<ImportExportMetadata>();

    metadata.push({ Name: 'startDate', Value: this.startDate });
    metadata.push({ Name: 'endDate', Value: this.endDate });

    this.importExportService.getTemplateFile(this.selectedScenarioId, this.selectedParentBuildId, this.selectedCapitalForecastLogId, this.selectedImportType, metadata)
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe(blob => {
        saveAs(blob.Blob, blob.FileNameOrDefault("DetailedForecast.xlsx"));
      });
  }

  public showDownloadDialog(importType: CEMImportTypes, skipDialog: boolean = false) {
    switch (importType) {
      case CEMImportTypes.DrillingForecastDetail:
        this.detailTypeDescription = "Drilling";
        break;
      case CEMImportTypes.CompletionsForecastDetail:
        this.detailTypeDescription = "Completions";
        break;
      case CEMImportTypes.WellConnectForecastDetail:
        this.detailTypeDescription = "Well Connect";
        break;
      case CEMImportTypes.PadConstructionForecastDetail:
        this.detailTypeDescription = "Pad Construction";
        break;
      default:
        break;
    }

    this.selectedImportType = importType;

    if (skipDialog) {
      this.downloadDetailedForecastSheet();
    } else {
      this.isShowingDownloadDialog = true;
    }
  }

  public updateCapitalConfirm(scenarioId: number) {
    this.loading = true;
    this.showUpdateCapitalForecastDialog = false;
    this.service.startCapitalForecast(scenarioId, this.effectiveDate, this.userComment).subscribe(s => {
      this.loading = false;

      const dialog: DialogRef = this.dialogService.open({
        title: `Update Capital`,
        content: `A capital forecast has been successfully queued to run for Scenario ${scenarioId}. You'll be notified via email when it completes. Note: Forecasts are run in the order they are sent to the processor.`,
        height: 135,
        actions: [
          { text: "OK", primary: true }
        ]
      });
      this.effectiveDate = moment().add(this.environment.capitalForecastDefaultEffectiveDateOffset, 'days').toDate();
      this.userComment = '';

    });
  }

  public onClickCloseCapital() {
    this.showUpdateCapitalForecastDialog = false;
    this.effectiveDate = moment().add(this.environment.capitalForecastDefaultEffectiveDateOffset, 'days').toDate();
    this.userComment = '';
  }
}